import { useEffect, useMemo, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import {
  useStripeTheme,
  splitCardElementStyle,
  splitElementsAppearance,
} from '../../lib/stripeTheme'

const PK = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY

let _stripePromise = null
function getStripe() {
  if (!PK) return null
  if (!_stripePromise) _stripePromise = loadStripe(PK)
  return _stripePromise
}

export default function StripeCardOnlyModal({
  clientSecret,
  onSuccess,
  onClose,
  prefill,
}) {
  const stripePromise = useMemo(() => getStripe(), [])
  const theme = useStripeTheme()

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!clientSecret) return null
  if (!stripePromise) {
    return (
      <div className="stripe-modal-backdrop" onClick={onClose}>
        <div className="stripe-modal" onClick={(e) => e.stopPropagation()}>
          <button
            className="stripe-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
          <div className="stripe-modal-title">Payment unavailable</div>
          <p className="stripe-modal-warning">
            Stripe publishable key is not configured.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="stripe-modal-backdrop" onClick={onClose}>
      <div
        className="stripe-modal stripe-modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="stripe-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <div className="stripe-modal-title">Add card for verification</div>
        <Elements
          key={theme}
          stripe={stripePromise}
          options={{
            appearance: splitElementsAppearance(theme),
            fonts: [
              {
                cssSrc:
                  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap',
              },
            ],
          }}
        >
          <SplitCardForm
            clientSecret={clientSecret}
            onSuccess={onSuccess}
            onClose={onClose}
            prefill={prefill}
            theme={theme}
          />
        </Elements>
      </div>
    </div>
  )
}

function SplitCardForm({ clientSecret, onSuccess, onClose, prefill, theme }) {
  const stripe = useStripe()
  const elements = useElements()
  const elementStyle = useMemo(() => splitCardElementStyle(theme), [theme])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [zip, setZip] = useState('')
  const [country, setCountry] = useState('US')
  const [numberReady, setNumberReady] = useState(false)
  const [expiryReady, setExpiryReady] = useState(false)
  const [cvcReady, setCvcReady] = useState(false)
  const ready = numberReady && expiryReady && cvcReady

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault()
    if (!stripe || !elements) return
    if (!zip.trim()) {
      setErr('Enter a postal/ZIP code')
      return
    }
    setBusy(true)
    setErr('')

    const cardNumber = elements.getElement(CardNumberElement)
    if (!cardNumber) {
      setErr('Card form not ready')
      setBusy(false)
      return
    }

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardNumber,
        billing_details: {
          email: prefill?.email || undefined,
          name: prefill?.name || undefined,
          address: {
            postal_code: zip.trim(),
            country: country || 'US',
          },
        },
      },
    })

    if (result.error) {
      setErr(result.error.message || 'Payment failed')
      setBusy(false)
      return
    }
    const piStatus = result.paymentIntent?.status
    if (piStatus === 'succeeded' || piStatus === 'processing') {
      if (onSuccess) onSuccess(result.paymentIntent)
      onClose()
    } else {
      setErr('Payment did not complete. Please try again.')
      setBusy(false)
    }
  }

  return (
    <div role="form" aria-label="Stripe card payment form">
      <div className="stripe-modal-warning">
        <strong>
          A $1 USD charge will be made to your card for verification and
          saved for future payments.
        </strong>{' '}
        It is refunded automatically within 3–5 business days.
      </div>

      <div
        style={{
          marginTop: 10,
          fontSize: 12,
          lineHeight: 1.5,
          color: 'var(--text-3)',
        }}
      >
        Heads up — if you use browser autofill or a saved wallet (1Password,
        Apple Pay, Brave/Chrome saved cards) the card fields may appear
        blank or hard to read after autofilling. The card data is still
        being entered correctly and will save successfully. For the
        cleanest experience, type your card details manually.
      </div>

      {!ready && (
        <div className="stripe-modal-loading">Loading payment form…</div>
      )}

      <div className="cco-fields">
        <div className="cco-field cco-field-full">
          <label className="cco-label">Card number</label>
          <div className="cco-input-wrap">
            <CardNumberElement
              options={{ ...elementStyle, showIcon: true, placeholder: '1234 1234 1234 1234' }}
              onReady={() => setNumberReady(true)}
            />
          </div>
        </div>

        <div className="cco-row-2col">
          <div className="cco-field">
            <label className="cco-label">Expiry</label>
            <div className="cco-input-wrap">
              <CardExpiryElement
                options={{ ...elementStyle, placeholder: 'MM / YY' }}
                onReady={() => setExpiryReady(true)}
              />
            </div>
          </div>
          <div className="cco-field">
            <label className="cco-label">CVC</label>
            <div className="cco-input-wrap">
              <CardCvcElement
                options={{ ...elementStyle, placeholder: 'CVC' }}
                onReady={() => setCvcReady(true)}
              />
            </div>
          </div>
        </div>

        <div className="cco-row-2col">
          <div className="cco-field">
            <label className="cco-label">Country</label>
            <div className="cco-input-wrap cco-input-wrap-native">
              <select
                className="cco-native"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="IN">India</option>
                <option value="MX">Mexico</option>
              </select>
            </div>
          </div>
          <div className="cco-field">
            <label className="cco-label">ZIP / Postal code</label>
            <div className="cco-input-wrap cco-input-wrap-native">
              <input
                className="cco-native"
                type="text"
                inputMode="text"
                autoComplete="postal-code"
                placeholder="12345"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {err && <div className="stripe-modal-error" style={{ marginTop: 12 }}>{err}</div>}

      <div className="stripe-modal-footer">
        <button
          type="button"
          className="stripe-modal-cancel"
          onClick={onClose}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="stripe-modal-submit"
          onClick={handleSubmit}
          disabled={busy || !stripe || !ready}
        >
          {busy ? 'Processing…' : 'Save card'}
        </button>
      </div>

      <div className="stripe-modal-lockup">
        <span>Powered by</span>
        <img src="/stripe-logo.png" alt="Stripe" />
      </div>
    </div>
  )
}
